import { Icon } from '@eliadmin/core/src/components/dlz/ui/icon';
import {
  PASSWORD_RULES,
  PASSWORD_STRENGTH_LABELS,
  passwordStrength,
} from '@eliadmin/core/src/components/dlz/forms/password-rules';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/**
 * Figma „Strength“ + „Requirements“ (60260:29901, 60260:29908): four 4 px bars (bg/brand-success while
 * filled, border/subtle otherwise) with the level right-aligned under them, then the requirement
 * list – met rules with the green check-circle, open ones with a grey dot. The designs show only the
 * medium state (3 bars, green); weaker levels use text/brand.
 */
export function PasswordStrength({
  value,
  showRules = true,
  className,
}: {
  value: string;
  showRules?: boolean;
  className?: string;
}) {
  const level = passwordStrength(value);
  const weak = level > 0 && level < 3;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-2">
        <div aria-hidden="true" className="grid grid-cols-4 gap-1">
          {[1, 2, 3, 4].map(bar => (
            <span
              key={bar}
              className={cn(
                'h-1 rounded-sm transition-colors',
                bar > level ? 'bg-[#E2E4E7]' : weak ? 'bg-[#D7191F]' : 'bg-[#4DB873]',
              )}
            />
          ))}
        </div>
        <p
          aria-live="polite"
          className={cn(
            'dlz-caption min-h-4 text-right',
            weak ? 'text-[#D7191F]' : 'text-[#16753A]',
          )}
        >
          {value ? <span className="sr-only">Sila hesla: </span> : null}
          {PASSWORD_STRENGTH_LABELS[level]}
        </p>
      </div>
      {showRules ? (
        <ul className="flex flex-col gap-1">
          {PASSWORD_RULES.map(rule => {
            const met = rule.test(value);
            return (
              <li
                key={rule.id}
                className={cn(
                  'dlz-body-s flex items-center gap-1.5',
                  met ? 'text-[#16753A]' : 'text-[#6B7079]',
                )}
              >
                {met ? (
                  <Icon name="check-circle" size={20} className="shrink-0 text-[#22A04B]" />
                ) : (
                  <span
                    aria-hidden="true"
                    className="flex size-5 shrink-0 items-center justify-center"
                  >
                    <span className="size-3.5 rounded-full bg-[#E2E4E7]" />
                  </span>
                )}
                {rule.label}
                <span className="sr-only">{met ? ' – splnené' : ' – nesplnené'}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}
